/**
 * Fonts Admin API client
 *
 * Endpoints live at /v1/admin/fonts/* — apiClient auto-prefixes /v1/admin.
 */

import { apiClient } from '@/lib/api/client'

export interface FontRead {
  id: number
  name: string
  display_name: string
  description: string | null
  object_name: string
  file_size: number | null
  mime_type: string
  is_global_default: boolean
  uploaded_by: string | null
  created_at: string
  updated_at: string
  deleted_at: string | null
  reference_count: number
  preview_url: string
}

export interface FontUpdateInput {
  display_name?: string
  description?: string
  is_global_default?: boolean
}

export interface FontUploadInput {
  file: File
  display_name: string
  description?: string
  name?: string
}

export const fontsApi = {
  async list(includeDeleted = false): Promise<FontRead[]> {
    const res = await apiClient.get<FontRead[]>('/fonts', {
      params: { include_deleted: includeDeleted },
    })
    return res.data
  },

  async get(id: number): Promise<FontRead> {
    const res = await apiClient.get<FontRead>(`/fonts/${id}`)
    return res.data
  },

  async upload(params: FontUploadInput): Promise<FontRead> {
    const fd = new FormData()
    fd.append('file', params.file)
    fd.append('display_name', params.display_name)
    if (params.description) fd.append('description', params.description)
    if (params.name) fd.append('name', params.name)
    const res = await apiClient.post<FontRead>('/fonts', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return res.data
  },

  async update(id: number, body: FontUpdateInput): Promise<FontRead> {
    const res = await apiClient.patch<FontRead>(`/fonts/${id}`, body)
    return res.data
  },

  async delete(id: number): Promise<{ deleted_at: string }> {
    const res = await apiClient.delete<{ deleted_at: string }>(`/fonts/${id}`)
    return res.data
  },

  async restore(id: number): Promise<FontRead> {
    const res = await apiClient.post<FontRead>(`/fonts/${id}/restore`)
    return res.data
  },

  /**
   * Fetch a preview PNG as a blob. Required because `<img src>` can't send
   * Authorization headers and the preview endpoint uses `require_admin`.
   */
  async previewBlob(id: number, text: string, size = 48): Promise<Blob> {
    const res = await apiClient.get(`/fonts/${id}/preview`, {
      params: { text, size },
      responseType: 'blob',
    })
    return res.data as Blob
  },
}
